/**
 * Satisfaction ratings — manually recorded, unlike every other metric on this
 * page.
 *
 * The question is "if Hourglass went away, how would users feel?", so the scale
 * is INVERTED relative to intuition: "Very disappointed" is the GOOD end (they
 * would hate to lose us) and "Happy" is the bad one. Nothing here may sort or
 * colour on the assumption that a cheerier word is better.
 *
 * Stored as the display string itself rather than a code. There is no lookup
 * table to drift out of sync, and a raw database row reads as English — worth
 * more than the bytes on a table this small.
 *
 * Backend: /admin/satisfaction, gated by requireHourglassInternal.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useApi } from "@/shared/utils/request";

export type SatisfactionScore =
	| "Very disappointed"
	| "Somewhat disappointed"
	| "Indifferent"
	| "Happy";

/**
 * Best to worst, which is the order the picker shows and the order sorting
 * uses. Reversing this array silently inverts the whole column.
 */
export const SATISFACTION_SCORES: SatisfactionScore[] = [
	"Very disappointed",
	"Somewhat disappointed",
	"Indifferent",
	"Happy",
];

/** One-line gloss shown beside each option in the help popover. */
export const SATISFACTION_HINTS: Record<SatisfactionScore, string> = {
	"Very disappointed": "Would fight to keep it",
	"Somewhat disappointed": "Would miss it",
	Indifferent: "Could take it or leave it",
	Happy: "Would be glad to see it go",
};

/**
 * Rank for sorting and comparison: 1 is the best outcome for us.
 *
 * Sorting cannot use the string itself — alphabetical order puts "Happy"
 * first, which is exactly backwards.
 */
export function satisfactionRank(score: string | null): number | null {
	if (score === null) return null;
	const index = SATISFACTION_SCORES.indexOf(score as SatisfactionScore);
	return index === -1 ? null : index + 1;
}

/**
 * Disappointed is good, happy is bad. Binary, not banded — a four-point forced
 * choice has no meaningful middle, and "Indifferent" is a churn signal rather
 * than a neutral one. Null is a note-only row, rendered neutrally.
 */
export function satisfactionClass(score: string | null): string {
	const rank = satisfactionRank(score);
	if (rank === null) return "bg-muted text-muted-foreground";
	return rank <= 2
		? "bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300"
		: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300";
}

export function isSatisfactionScore(v: string): v is SatisfactionScore {
	return (SATISFACTION_SCORES as string[]).includes(v);
}

/** Wire shape from the API — camelCase, `createdAt` is a full ISO timestamp. */
export interface SatisfactionEntry {
	id: string;
	profId: string;
	score: string | null;
	note: string | null;
	createdAt: string;
}

/**
 * A row reached through the SCORE ranking (`WHERE score IS NOT NULL`), so its
 * score is guaranteed. Its note is not — a score can be recorded without one.
 */
export interface ScoredEntry extends SatisfactionEntry {
	score: string;
}

/**
 * A row reached through the NOTE ranking (`WHERE note IS NOT NULL`), so its
 * note is guaranteed. Its score is not — this may be a note-only row, and it
 * may or may not be the same row as `current`.
 */
export interface NotedEntry extends SatisfactionEntry {
	note: string;
}

/**
 * Scores and notes are ranked SEPARATELY by the server, over two different
 * filtered sets: current/previous are the latest two rows carrying a score,
 * latestNote is the latest row carrying a note.
 *
 * That separation is what stops a note-only row from blanking the score
 * column — ranking both off "newest row" would make writing a note today erase
 * yesterday's score from the table.
 *
 * All three are independently nullable. A timekeeper may have notes and no
 * score, a score and no notes, or one score and no previous.
 */
export interface SatisfactionPair {
	/**
	 * Onboarding state from the timekeeper's newest row, whatever that row is.
	 *
	 * A timekeeper with no rows at all is ABSENT from the map — there is no
	 * `onboarded: false`. Read a missing key as not onboarded: zero-activity
	 * timekeepers are seeded into the table from the licensed list and have no
	 * satisfaction row, so that is the common case, not an edge one.
	 */
	onboarded?: boolean;
	/**
	 * Id of the row `onboarded` was read from — the newest row for this
	 * timekeeper, whatever it carries. This is what a toggle PATCHes.
	 *
	 * Comes from the server's unfiltered ranking, NOT from comparing `current`
	 * and `latestNote`: rows carrying only an onboarded flag appear in neither
	 * of those two rankings, so deriving it here would go stale on exactly the
	 * zero-activity timekeepers this feature is for.
	 */
	latestId?: string;
	current: ScoredEntry | null;
	previous: ScoredEntry | null;
	latestNote: NotedEntry | null;
}

export type SatisfactionMap = Record<string, SatisfactionPair>;

const KEY = ["internal", "satisfaction"] as const;

export function useSatisfaction(profIds: string[]) {
	const { makeApiRequest } = useApi();
	const queryClient = useQueryClient();

	// Sorted and joined so a reordering upstream doesn't refetch.
	const profIdsKey = [...profIds].sort().join(",");

	const query = useQuery({
		queryKey: [...KEY, profIdsKey] as const,
		enabled: profIdsKey.length > 0,
		queryFn: async ({ signal }) => {
			const res = await makeApiRequest({
				endpoint: `/admin/satisfaction?profIds=${encodeURIComponent(profIdsKey)}`,
				method: "GET",
				otherOptions: { signal },
			});
			if (res.error) throw new Error(String(res.error));
			return (res.data ?? {}) as SatisfactionMap;
		},
	});

	const mutation = useMutation({
		mutationFn: async (input: {
			profId: string;
			score: SatisfactionScore;
			note?: string | null;
		}) => {
			const res = await makeApiRequest({
				endpoint: "/admin/satisfaction",
				method: "POST",
				body: {
					prof_id: input.profId,
					score: input.score,
					note: input.note ?? null,
				},
			});
			if (res.error) throw new Error(String(res.error));
			return res.data as SatisfactionEntry;
		},
		// POST returns only the created row, not the new current/previous pair,
		// so the list has to be refetched to re-rank them.
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: KEY });
		},
	});

	/**
	 * Edit the note on the CURRENT entry.
	 *
	 * PATCH, never POST: a note is context for the score that already exists,
	 * not a new observation. Inserting a row would push the real previous score
	 * out of the comparison and make the table read as though the score had
	 * just changed when only its note did.
	 */
	const updateNote = useMutation({
		mutationFn: async (input: { entryId: string | null; profId: string; note: string }) => {
			const note = input.note.trim() === "" ? null : input.note.trim();

			// PATCH the row the note already lives on, so editing text never spawns
			// a history entry. With no note row yet, POST a note-only row — the
			// server ranks it out of the score columns, so it cannot disturb them.
			const res = input.entryId
				? await makeApiRequest({
						endpoint: `/admin/satisfaction/${encodeURIComponent(input.entryId)}`,
						method: "PATCH",
						body: { note },
					})
				: await makeApiRequest({
						endpoint: "/admin/satisfaction",
						method: "POST",
						body: { prof_id: input.profId, note },
					});

			if (res.error) throw new Error(String(res.error));
			return res.data as SatisfactionEntry;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: KEY });
		},
	});

	/**
	 * Flip a timekeeper's onboarded flag.
	 *
	 * PATCHes the newest row when there is one. With no row at all there is
	 * nothing to patch, so it POSTs one carrying only the flag — the server
	 * accepts that since an explicitly supplied `onboarded` counts as content.
	 */
	const onboarding = useMutation({
		mutationFn: async (input: { profId: string; onboarded: boolean }) => {
			const latestId = query.data?.[input.profId]?.latestId ?? null;
			// 0 or 1, never a boolean: the server validates strictly and a stored
			// value outside that range would read back as `true` with no trace.
			const onboarded = input.onboarded ? 1 : 0;

			const res = latestId
				? await makeApiRequest({
						endpoint: `/admin/satisfaction/${encodeURIComponent(latestId)}`,
						method: "PATCH",
						body: { onboarded },
					})
				: await makeApiRequest({
						endpoint: "/admin/satisfaction",
						method: "POST",
						body: { prof_id: input.profId, onboarded },
					});

			if (res.error) throw new Error(String(res.error));
			return res.data as SatisfactionEntry;
		},
		// Returned, not fire-and-forget, so the mutation does not settle until the
		// refetch lands. `setOnboarded`'s in-flight guard depends on that: it has
		// to stay closed until `latestId` is actually back.
		onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
	});

	/**
	 * Timekeepers with an onboarding write in flight.
	 *
	 * A ref, not state: this gates an event handler and must be current on the
	 * very next call, without waiting for a render.
	 */
	const onboardingInFlight = useRef(new Set<string>());

	const setOnboarded = useCallback(
		async (profId: string, onboarded: boolean) => {
			// A second double-click before the refetch lands would still see no
			// `latestId` and POST a SECOND onboarded-only row, since the decision
			// between POST and PATCH is made from cached data. Double-click is the
			// trigger here, so repeat activation is the expected input, not an
			// unlucky one.
			if (onboardingInFlight.current.has(profId)) return false;

			// Nothing to record: a timekeeper with no row already reads as not
			// onboarded, and writing a row to say so would be noise in the history.
			if (!onboarded && !query.data?.[profId]?.latestId) return false;

			onboardingInFlight.current.add(profId);
			try {
				await onboarding.mutateAsync({ profId, onboarded });
				return true;
			} finally {
				onboardingInFlight.current.delete(profId);
			}
		},
		[query.data, onboarding],
	);

	/**
	 * Record a score, but only when it actually changed.
	 *
	 * Re-entering the same number is a no-op: the table is a history of
	 * CHANGES, and writing an identical row would add a meaningless entry and
	 * reset the "previous" comparison to today.
	 */
	const record = useCallback(
		async (profId: string, score: SatisfactionScore) => {
			const currentScore = query.data?.[profId]?.current?.score;
			if (currentScore === score) return false;
			await mutation.mutateAsync({ profId, score });
			return true;
		},
		[query.data, mutation],
	);

	/**
	 * Save or clear a note.
	 *
	 * Clearing the last note on a row is allowed. The server used to reject a
	 * row carrying neither a score nor a note, because such a row ranked in
	 * neither window and sat invisibly in history — that guard is gone now that
	 * an onboarded flag makes the row meaningful and visible on its own.
	 *
	 * Also allowed with no score at all: a note can be the first thing recorded
	 * about a timekeeper.
	 */
	const saveNote = useCallback(
		async (profId: string, note: string) => {
			const entryId = query.data?.[profId]?.latestNote?.id ?? null;
			await updateNote.mutateAsync({ entryId, profId, note });
			return true;
		},
		[query.data, updateNote],
	);

	return {
		scores: query.data ?? {},
		isLoading: query.isLoading,
		error: query.error instanceof Error ? query.error.message : null,
		record,
		saveNote,
		setOnboarded,
		isSaving: mutation.isPending,
		isSavingNote: updateNote.isPending,
		isSavingOnboarded: onboarding.isPending,
		saveError:
			mutation.error instanceof Error
				? mutation.error.message
				: updateNote.error instanceof Error
					? updateNote.error.message
					: onboarding.error instanceof Error
						? onboarding.error.message
						: null,
	};
}

/** Full history for one timekeeper, newest first. Fetched only when opened. */
export function useSatisfactionHistory(profId: string | null) {
	const { makeApiRequest } = useApi();

	return useQuery({
		queryKey: [...KEY, "history", profId] as const,
		enabled: !!profId,
		queryFn: async ({ signal }) => {
			const res = await makeApiRequest({
				endpoint: `/admin/satisfaction/${encodeURIComponent(profId ?? "")}/history`,
				method: "GET",
				otherOptions: { signal },
			});
			if (res.error) throw new Error(String(res.error));
			return (res.data ?? []) as SatisfactionEntry[];
		},
	});
}
